import { useState } from "react";
import { useLang } from "@/contexts/LangContext";
import { api } from "@/lib/api";
import {
  AlertTriangle, Database, FileText, FileJson, Printer,
  ShieldAlert, CheckCircle2, Lock,
} from "lucide-react";
import { toast } from "sonner";

export default function DangerZone({ onResetDone }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState("all"); // 'all' or 'date_range'
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [includePayouts, setIncludePayouts] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [password, setPassword] = useState("");
  const [backupDone, setBackupDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function backupJson() {
    try {
      const params = {};
      if (scope === "date_range") {
        if (from) params.date_from = from;
        if (to) params.date_to = to;
      }
      const { data } = await api.get("/admin/backup", { params });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      downloadBlob(blob, `Java-SPA_Backup_${stamp}.json`);
      setBackupDone(true);
      toast.success(t("danger.backupDone"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error backup");
    }
  }

  async function backupCsv() {
    try {
      const params = {};
      if (scope === "date_range") {
        if (from) params.date_from = from;
        if (to) params.date_to = to;
      }
      const { data } = await api.get("/admin/backup", { params });
      const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      const lines = [];
      lines.push("sep=,");
      lines.push(esc("JAVA SPA · BACKUP LAPORAN KEUANGAN"));
      lines.push([esc("Generated"), esc(data.generated_at)].join(","));
      lines.push([esc("Periode"),
        esc(scope === "date_range" ? `${from || "—"} s/d ${to || "—"}` : "Semua")].join(","));
      lines.push([esc("Total Transaksi"), esc(data.counts.transactions)].join(","));
      lines.push([esc("Total Refunds"), esc(data.counts.refunds)].join(","));
      lines.push([esc("Total Payouts"), esc(data.counts.commission_payouts)].join(","));
      lines.push("");
      lines.push("===== TRANSACTIONS =====");
      lines.push(["id", "date", "customer_phone", "subtotal", "discount", "total", "cashier", "cancelled", "cancel_reason"].map(esc).join(","));
      data.transactions.forEach((t) => {
        lines.push([
          esc(t.id), esc(t.created_at), esc(t.customer_phone),
          esc(t.subtotal), esc(t.discount_amount || 0), esc(t.total),
          esc(t.cashier_name), esc(!!t.cancelled), esc(t.cancel_reason || ""),
        ].join(","));
      });
      lines.push("");
      lines.push("===== TRANSACTION ITEMS =====");
      lines.push(["tx_id", "service", "therapist", "price", "is_upsell", "commission_base", "commission_upsell"].map(esc).join(","));
      data.transactions.forEach((t) => {
        (t.items || []).forEach((it) => {
          lines.push([
            esc(t.id), esc(it.service_name_id), esc(it.therapist_name),
            esc(it.price), esc(!!it.is_upsell),
            esc(it.commission_base || 0), esc(it.commission_upsell || 0),
          ].join(","));
        });
      });
      lines.push("");
      lines.push("===== REFUNDS =====");
      lines.push(["id", "transaction_id", "amount", "reason", "notes"].map(esc).join(","));
      data.refunds.forEach((r) => {
        lines.push([esc(r.id), esc(r.transaction_id), esc(r.refund_amount), esc(r.reason), esc(r.notes || "")].join(","));
      });
      lines.push("");
      lines.push("===== COMMISSION PAYOUTS =====");
      lines.push(["id", "therapist_name", "amount", "paid_at", "paid_by_name", "notes"].map(esc).join(","));
      data.commission_payouts.forEach((p) => {
        lines.push([
          esc(p.id), esc(p.therapist_name), esc(p.amount),
          esc(p.paid_at), esc(p.paid_by_name), esc(p.notes || ""),
        ].join(","));
      });

      const blob = new Blob(["\uFEFF" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      downloadBlob(blob, `Java-SPA_Backup_${stamp}.csv`);
      setBackupDone(true);
      toast.success(t("danger.backupDone"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error backup");
    }
  }

  async function backupPdf() {
    try {
      const params = {};
      if (scope === "date_range") {
        if (from) params.date_from = from;
        if (to) params.date_to = to;
      }
      const { data } = await api.get("/admin/backup", { params });
      const periodLabel = scope === "date_range"
        ? `${from || "—"} s/d ${to || "—"}`
        : "Semua Transaksi";
      const fmtDate = (iso) => {
        try { return new Date(iso).toLocaleString("id-ID", { timeZone: "Asia/Riyadh" }); }
        catch { return iso || "—"; }
      };
      const fmtMoney = (n) => `SAR ${Number(n || 0).toFixed(2)}`;
      const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

      const totalRevenue = data.transactions.filter((t) => !t.cancelled).reduce((s, t) => s + (t.total || 0), 0);
      const cashCount = data.transactions.filter((t) => !t.cancelled && (t.payment_method || "cash") === "cash").length;
      const cardCount = data.transactions.filter((t) => !t.cancelled && t.payment_method === "card").length;
      const cashTotal = data.transactions.filter((t) => !t.cancelled && (t.payment_method || "cash") === "cash").reduce((s, t) => s + t.total, 0);
      const cardTotal = data.transactions.filter((t) => !t.cancelled && t.payment_method === "card").reduce((s, t) => s + t.total, 0);

      const txRows = data.transactions.map((tx, i) => `
        <tr ${tx.cancelled ? 'style="opacity:0.5;text-decoration:line-through"' : ""}>
          <td>${i + 1}</td>
          <td>${esc(fmtDate(tx.created_at))}</td>
          <td>${esc(tx.customer_phone)}</td>
          <td>${esc((tx.items || []).map((it) => it.service_name_id).join(", "))}</td>
          <td>${esc(tx.cashier_name || "")}</td>
          <td style="text-transform:uppercase;font-size:9px">${(tx.payment_method || "cash") === "card" ? "Kartu" : "Tunai"}</td>
          <td style="text-align:right">${fmtMoney(tx.subtotal)}</td>
          <td style="text-align:right">${fmtMoney(tx.discount_amount || 0)}</td>
          <td style="text-align:right;font-weight:600">${fmtMoney(tx.total)}</td>
          <td>${tx.cancelled ? `Batal: ${esc(tx.cancel_reason || "")}` : ""}</td>
        </tr>`).join("");

      const refundRows = data.refunds.map((r, i) => `
        <tr>
          <td>${i + 1}</td>
          <td>${esc(fmtDate(r.created_at))}</td>
          <td>${esc(r.transaction_id)}</td>
          <td>${esc(r.reason)}</td>
          <td style="text-align:right">${fmtMoney(r.refund_amount)}</td>
          <td>${esc(r.notes || "")}</td>
        </tr>`).join("");

      const payoutRows = data.commission_payouts.map((p, i) => `
        <tr>
          <td>${i + 1}</td>
          <td>${esc(fmtDate(p.paid_at))}</td>
          <td>${esc(p.therapist_name)}</td>
          <td style="text-align:right">${fmtMoney(p.amount)}</td>
          <td>${esc(p.paid_by_name || "")}</td>
          <td>${esc(p.notes || "")}</td>
        </tr>`).join("");

      const html = `<!doctype html>
<html lang="id"><head><meta charset="utf-8">
<title>Java SPA · Backup Laporan Keuangan</title>
<style>
  @page { size: A4 landscape; margin: 12mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #111; background:#fff; padding: 8px 16px; }
  h1 { font-size: 22px; margin: 0 0 4px; color: #B8901E; }
  h2 { font-size: 14px; margin: 18px 0 8px; color: #B8901E; border-bottom: 2px solid #f0e9c8; padding-bottom: 4px; }
  .meta { font-size: 11px; color: #555; line-height: 1.6; margin-bottom: 12px; }
  .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin: 14px 0; }
  .summary .box { border: 1px solid #e2e2dd; padding: 8px 10px; border-radius: 6px; background:#fafaf6; }
  .summary .label { font-size: 9px; letter-spacing: 1.5px; color: #777; text-transform: uppercase; }
  .summary .value { font-size: 16px; font-weight: 700; color: #1a1a1a; margin-top: 2px; }
  table { width: 100%; border-collapse: collapse; font-size: 10px; margin-top: 4px; page-break-inside: auto; }
  thead { background: #FFF8E1; color: #B8901E; }
  th, td { padding: 5px 7px; border: 1px solid #e6e2d4; text-align: left; vertical-align: top; }
  th { font-size: 9px; letter-spacing: 1px; text-transform: uppercase; }
  tr { page-break-inside: avoid; page-break-after: auto; }
  .footer { margin-top: 20px; text-align: center; font-size: 9px; color: #999; border-top: 1px solid #e6e2d4; padding-top: 8px; }
  @media print {
    body { padding: 0; }
    .no-print { display: none !important; }
  }
  .toolbar { position: sticky; top: 0; background:#fff; padding: 8px 0; border-bottom: 1px solid #eee; margin-bottom: 8px; }
  .btn { padding: 6px 14px; border-radius: 999px; background: #B8901E; color: #fff; border: 0; font-weight: 600; cursor: pointer; font-size: 12px; }
</style>
</head><body>
<div class="toolbar no-print">
  <button class="btn" onclick="window.print()">🖨️ Cetak / Simpan PDF</button>
</div>
<h1>JAVA SPA — Backup Laporan Keuangan</h1>
<div class="meta">
  <div><b>Periode:</b> ${esc(periodLabel)}</div>
  <div><b>Digenerate:</b> ${esc(fmtDate(data.generated_at))}</div>
  <div><b>Filter:</b> ${esc(JSON.stringify(data.filter))}</div>
</div>

<div class="summary">
  <div class="box"><div class="label">Total Transaksi</div><div class="value">${data.counts.transactions}</div></div>
  <div class="box"><div class="label">Total Pendapatan</div><div class="value">${fmtMoney(totalRevenue)}</div></div>
  <div class="box"><div class="label">Tunai (${cashCount} tx)</div><div class="value">${fmtMoney(cashTotal)}</div></div>
  <div class="box"><div class="label">Kartu (${cardCount} tx)</div><div class="value">${fmtMoney(cardTotal)}</div></div>
</div>

<h2>Transaksi (${data.counts.transactions})</h2>
${data.counts.transactions === 0 ? '<div class="meta">Tidak ada data transaksi.</div>' : `
<table>
  <thead><tr><th>#</th><th>Tanggal</th><th>No. HP</th><th>Layanan</th><th>Kasir</th><th>Bayar</th><th style="text-align:right">Subtotal</th><th style="text-align:right">Diskon</th><th style="text-align:right">Total</th><th>Catatan</th></tr></thead>
  <tbody>${txRows}</tbody>
</table>`}

<h2>Refunds (${data.counts.refunds})</h2>
${data.counts.refunds === 0 ? '<div class="meta">Tidak ada data refund.</div>' : `
<table>
  <thead><tr><th>#</th><th>Tanggal</th><th>Transaksi ID</th><th>Alasan</th><th style="text-align:right">Jumlah</th><th>Catatan</th></tr></thead>
  <tbody>${refundRows}</tbody>
</table>`}

<h2>Pembayaran Komisi (${data.counts.commission_payouts})</h2>
${data.counts.commission_payouts === 0 ? '<div class="meta">Tidak ada data pembayaran komisi.</div>' : `
<table>
  <thead><tr><th>#</th><th>Tanggal Bayar</th><th>Terapis</th><th style="text-align:right">Jumlah</th><th>Dibayarkan oleh</th><th>Catatan</th></tr></thead>
  <tbody>${payoutRows}</tbody>
</table>`}

<div class="footer">Java SPA · Riyadh, Kingdom of Saudi Arabia · +966 55 222 1465</div>
<script>setTimeout(() => window.print(), 400);</script>
</body></html>`;

      const w = window.open("", "_blank");
      if (!w) {
        toast.error("Pop-up diblokir browser. Izinkan pop-up untuk cetak PDF.");
        return;
      }
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      w.location.replace(url);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      setBackupDone(true);
      toast.success(t("danger.backupDone"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error backup PDF");
    }
  }

  async function executeReset() {
    if (!backupDone) {
      toast.error(t("danger.backupFirst"));
      return;
    }
    if (confirmText.trim().toUpperCase() !== "RESET") {
      toast.error(t("danger.confirmType"));
      return;
    }
    if (!password) {
      toast.error(t("danger.adminPwd"));
      return;
    }
    if (scope === "date_range" && !from && !to) {
      toast.error("Pilih rentang tanggal");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        password,
        scope,
        confirm_text: confirmText,
        include_payouts: includePayouts,
      };
      if (scope === "date_range") {
        if (from) body.date_from = from;
        if (to) body.date_to = to;
      }
      const { data } = await api.post("/admin/reset", body);
      toast.success(
        `${t("danger.resetSuccess")} ${data.deleted_transactions} · Refunds: ${data.deleted_refunds} · Payouts: ${data.deleted_payouts}`
      );
      setOpen(false);
      setConfirmText("");
      setPassword("");
      setBackupDone(false);
      onResetDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset gagal");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="card-luxury p-5 border-2 border-red-500/30" data-testid="danger-zone-section">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-red-500/15 text-red-400 grid place-items-center shrink-0">
          <ShieldAlert className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-heading text-red-400">⚠️ {t("danger.title")}</h2>
          <p className="text-xs text-foreground/60 mt-0.5">{t("danger.subtitle")}</p>

          {!open ? (
            <button
              onClick={() => setOpen(true)}
              data-testid="danger-zone-open"
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-red-500/40 text-red-400 hover:bg-red-500/10 text-sm transition"
            >
              <AlertTriangle className="h-4 w-4" /> Buka Zona Bahaya
            </button>
          ) : (
            <div className="mt-5 space-y-5">
              {/* SCOPE */}
              <div>
                <label className="block text-[10px] uppercase tracking-[0.2em] text-foreground/50 mb-2">
                  {t("danger.scope")}
                </label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setScope("all")}
                    data-testid="danger-scope-all"
                    className={`px-3 py-1.5 rounded-full text-xs border transition ${
                      scope === "all"
                        ? "border-gold bg-gold/10 text-gold"
                        : "border-charcoal-600 text-foreground/60 hover:border-gold/40"
                    }`}
                  >
                    {t("danger.scopeAll")}
                  </button>
                  <button
                    onClick={() => setScope("date_range")}
                    data-testid="danger-scope-range"
                    className={`px-3 py-1.5 rounded-full text-xs border transition ${
                      scope === "date_range"
                        ? "border-gold bg-gold/10 text-gold"
                        : "border-charcoal-600 text-foreground/60 hover:border-gold/40"
                    }`}
                  >
                    {t("danger.scopeRange")}
                  </button>
                </div>
                {scope === "date_range" && (
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <input
                      type="date"
                      value={from}
                      onChange={(e) => setFrom(e.target.value)}
                      data-testid="danger-from"
                      className="bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2 text-sm"
                    />
                    <input
                      type="date"
                      value={to}
                      onChange={(e) => setTo(e.target.value)}
                      data-testid="danger-to"
                      className="bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                )}
              </div>

              {/* BACKUP */}
              <div className="bg-charcoal-900/40 border border-gold/15 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Database className="h-4 w-4 text-gold" />
                  <div className="text-sm font-medium text-gold">{t("danger.backup")}</div>
                  {backupDone && (
                    <span className="ms-auto inline-flex items-center gap-1 text-[11px] text-leaf">
                      <CheckCircle2 className="h-3.5 w-3.5" /> {t("danger.backupDone")}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={backupCsv}
                    data-testid="danger-backup-csv"
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-gold/15 hover:bg-gold/25 text-gold text-xs transition"
                  >
                    <FileText className="h-3.5 w-3.5" /> {t("danger.backupCsv")}
                  </button>
                  <button
                    onClick={backupJson}
                    data-testid="danger-backup-json"
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-leaf/15 hover:bg-leaf/25 text-leaf text-xs transition"
                  >
                    <FileJson className="h-3.5 w-3.5" /> {t("danger.backupJson")}
                  </button>
                  <button
                    onClick={backupPdf}
                    data-testid="danger-backup-pdf"
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-charcoal-800 border border-gold/20 hover:bg-gold/10 text-foreground text-xs transition"
                  >
                    <Printer className="h-3.5 w-3.5" /> {t("danger.backupPdf")}
                  </button>
                </div>
              </div>

              {/* INCLUDE PAYOUTS */}
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={includePayouts}
                  onChange={(e) => setIncludePayouts(e.target.checked)}
                  data-testid="danger-include-payouts"
                  className="accent-red-500"
                />
                <span className="text-foreground/80">{t("danger.includePayouts")}</span>
              </label>

              {/* RESET FORM */}
              <div className="bg-red-500/5 border border-red-500/30 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-400" />
                  <div className="text-sm font-medium text-red-400">{t("danger.warning")}</div>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-foreground/50 mb-1">
                    {t("danger.confirmType")}
                  </label>
                  <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="RESET"
                    data-testid="danger-confirm-input"
                    className="w-full bg-charcoal-900 border border-red-500/40 rounded-md px-3 py-2 font-mono"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] text-foreground/50 mb-1">
                    <Lock className="inline h-3 w-3 me-1" /> {t("danger.adminPwd")}
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    data-testid="danger-pwd-input"
                    className="w-full bg-charcoal-900 border border-red-500/40 rounded-md px-3 py-2"
                  />
                </div>
                <button
                  onClick={executeReset}
                  disabled={submitting || !backupDone || confirmText.trim().toUpperCase() !== "RESET" || !password}
                  data-testid="danger-execute-btn"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-red-500 hover:bg-red-600 text-white font-medium text-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ShieldAlert className="h-4 w-4" />
                  {submitting ? "..." : t("danger.execute")}
                </button>
                {!backupDone && (
                  <div className="text-[11px] text-red-300/80 text-center">
                    {t("danger.backupFirst")}
                  </div>
                )}
              </div>

              <button
                onClick={() => setOpen(false)}
                className="text-xs text-foreground/50 hover:text-foreground"
              >
                Tutup
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
